import { RiPriceTag3Line } from '@remixicon/react';
import { useMemo } from 'react';
import { AgAutocomplete, AgButton, AgInput, AgModal, AgModalActions, AgModalContent } from 'src/components/ui';
import LabelTypeSelect from './LabelTypeSelect';
import labelColors from './labelColors';
import { useLabelDefinitionsStore } from './labelDefinitions.store';
import { useDeleteLabelDefinition } from './useDeleteLabelDefinition';
import { useUpdateLabelDefinition } from './useUpdateLabelDefinition';

export default function EditLabelModal() {
  const labelEditingState = useLabelDefinitionsStore((state) => state.labelEditing);
  const editLabel = useLabelDefinitionsStore((state) => state.editLabel);
  const saveEditingLabel = useLabelDefinitionsStore((state) => state.saveEditingLabel);
  const cancelEditingLabel = useLabelDefinitionsStore((state) => state.cancelEditingLabel);
  const deleteEditingLabel = useLabelDefinitionsStore((state) => state.deleteEditingLabel);
  const allLabels = useLabelDefinitionsStore((state) => {
    const allLabels = state.getAllLabels();

    if (labelEditingState?.old?.label) {
      allLabels.delete(labelEditingState.old.label);
    }

    return allLabels;
  });

  const hasDuplicate = allLabels.has(labelEditingState?.new?.label.trim());
  const isEmpty = labelEditingState?.new?.label?.trim() === '';
  const errorMessage = hasDuplicate ? 'Label name must be unique' : isEmpty ? 'Label name must not be empty' : '';

  const { remove: deleteLabelDefinition } = useDeleteLabelDefinition();
  const { update: updateLabelDefinition } = useUpdateLabelDefinition();

  const colorSelectorValue = useMemo(() => {
    const presetColor = labelColors.entries.find((e) => e.color === labelEditingState?.new?.color);

    if (presetColor) {
      return presetColor;
    }

    if (labelEditingState?.new?.color) {
      return { color: labelEditingState.new.color, name: 'Custom' };
    }

    return null;
  }, [labelEditingState?.new?.color]);

  return (
    <AgModal open={!!labelEditingState} onClose={cancelEditingLabel} title="Edit Label" size="md">
      <AgModalContent>
        <div className="flex flex-col gap-4 py-1">
          <AgInput
            label="Label Name"
            value={labelEditingState?.new?.label}
            onChange={(v) => editLabel('label', v)}
            isInvalid={!!errorMessage}
            errorMessage={errorMessage}
          />
          <AgAutocomplete
            label="Color"
            value={colorSelectorValue}
            options={labelColors.entries}
            onChange={(value) => editLabel('color', value?.color)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.color === value.color}
            startContent={
              labelEditingState?.new?.color && <RiPriceTag3Line size={20} color={labelEditingState.new.color} />
            }
            renderOption={(option) => (
              <div className="flex items-center gap-2">
                <RiPriceTag3Line size={20} color={option.color} />
                <span>{option.name}</span>
              </div>
            )}
          />
          <LabelTypeSelect />
        </div>
      </AgModalContent>
      <AgModalActions
        cancel={
          <AgButton
            className="mr-auto text-error-main"
            variant="ghost"
            onPress={() => {
              const isRemoteLabel = deleteEditingLabel();

              if (isRemoteLabel && labelEditingState.old.id) {
                deleteLabelDefinition(labelEditingState.old.id);
              }

              cancelEditingLabel();
            }}
          >
            Delete
          </AgButton>
        }
      >
        <AgButton
          variant="primary"
          onPress={() => {
            const isRemoteLabel = saveEditingLabel();
            const { color, label, labelTypes } = labelEditingState.new;

            if (isRemoteLabel && labelEditingState.old.id) {
              updateLabelDefinition({ id: labelEditingState.old.id, input: { color, label, labelTypes } });
            }

            cancelEditingLabel();
          }}
          isDisabled={!!errorMessage}
        >
          Save
        </AgButton>
      </AgModalActions>
    </AgModal>
  );
}
